/**
 * Custom postprocessing Effect that applies a CSS-spec-accurate color matrix.
 *
 * Replaces BrightnessContrast + HueSaturation + Sepia with a single shader pass
 * that matches CSS filter rendering exactly.
 */

import { Effect, BlendFunction } from 'postprocessing'
import { Uniform, Matrix4, Vector4 } from 'three'
import type { ColorMatrix } from '../utils/cssColorMatrix'

const fragmentShader = /* glsl */ `
uniform mat4 colorMatrix;
uniform vec4 colorOffset;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
    vec3 color = (colorMatrix * vec4(inputColor.rgb, 1.0)).rgb + colorOffset.rgb;
    outputColor = vec4(clamp(color, 0.0, 1.0), inputColor.a);
}
`

export class CSSColorMatrixEffect extends Effect {
  constructor(colorMatrix?: ColorMatrix) {
    const matrix = new Matrix4()
    const offset = new Vector4()

    if (colorMatrix) {
      matrix.fromArray(colorMatrix.matrix)
      offset.fromArray(colorMatrix.offset)
    } else {
      matrix.identity()
    }

    super('CSSColorMatrix', fragmentShader, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([
        ['colorMatrix', new Uniform(matrix)],
        ['colorOffset', new Uniform(offset)],
      ]),
    })
  }

  setColorMatrix(colorMatrix: ColorMatrix): void {
    const matUniform = this.uniforms.get('colorMatrix')!
    const offUniform = this.uniforms.get('colorOffset')!
    ;(matUniform.value as Matrix4).fromArray(colorMatrix.matrix)
    ;(offUniform.value as Vector4).fromArray(colorMatrix.offset)
  }
}
